import React, { useState, useEffect, useMemo, useRef } from 'react';
import './TripReport.css';
import ContentWrapper from './ContentWrapper';
import UniformTable from './UniformTable';
import FilterDropdown from './FilterDropdown';
import { toast } from 'react-toastify';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import useFleetGoogleMaps from '../utils/useFleetGoogleMaps';

const toUtcStamp = dt => {
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const d = String(dt.getUTCDate()).padStart(2, '0');
  const hh = String(dt.getUTCHours()).padStart(2, '0');
  const mm = String(dt.getUTCMinutes()).padStart(2, '0');
  const ss = String(dt.getUTCSeconds()).padStart(2, '0');
  return `${y}${m}${d}${hh}${mm}${ss}`;
};

const formatIstTime = value => {
  if (!value) return '-';
  const s = String(value);
  if (s.length < 14) return '-';
  const y = s.slice(0, 4), m = s.slice(4, 6), d = s.slice(6, 8), hh = s.slice(8, 10), mm = s.slice(10, 12), ss = s.slice(12, 14);
  // Input is UTC, convert to Date object
  const date = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d), Number(hh), Number(mm), Number(ss)));
  if (Number.isNaN(date.getTime())) return '-';
  // Show in IST
  return date.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

const formatDuration = seconds => {
  if (!seconds) return '0m';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
};

const buildPresetRange = preset => {
  const now = new Date();
  let durationMs = 4 * 60 * 60 * 1000;
  if (preset === '12h') durationMs = 12 * 60 * 60 * 1000;
  if (preset === '24h') durationMs = 24 * 60 * 60 * 1000;
  if (preset === '7d') durationMs = 7 * 24 * 60 * 60 * 1000;
  const start = new Date(now.getTime() - durationMs);
  return { start, end: now, label: preset };
};

  const TripMapPreview = ({ startLat, startLng, endLat, endLng }) => {
  const mapRef = useRef(null);

  const { isLoaded: mapsLoaded } = useFleetGoogleMaps();

  useEffect(() => {
    if (!mapsLoaded || !mapRef.current || !window.google || !window.google.maps) return;

    const gm = window.google && window.google.maps ? window.google.maps : null;
    const DirectionsServiceCtor = gm && gm.DirectionsService ? gm.DirectionsService : null;
    const DirectionsRendererCtor = gm && gm.DirectionsRenderer ? gm.DirectionsRenderer : null;
    const directionsService = DirectionsServiceCtor ? new DirectionsServiceCtor() : null;
    const directionsRenderer = DirectionsRendererCtor
      ? new DirectionsRendererCtor({
        polylineOptions: {
          strokeColor: '#3b82f6', // Blue color for the road route
          strokeWeight: 5,
          strokeOpacity: 0.8
        },
        suppressMarkers: true // We'll handle markers ourselves for better control
      })
      : null;

    const map = new gm.Map(mapRef.current, {
      zoom: 14,
      center: { lat: startLat, lng: startLng },
      disableDefaultUI: true,
      gestureHandling: 'none',
      clickableIcons: false,
      mapId: 'DEMO_MAP_ID' // Use a map ID to potentially use advanced markers/cleaner UI
    });

    if (directionsRenderer) directionsRenderer.setMap(map);

    // Always add markers first as fallback
    new gm.Marker({
      position: { lat: startLat, lng: startLng },
      map: map,
      icon: {
        path: gm.SymbolPath.CIRCLE,
        fillColor: '#22c55e',
        fillOpacity: 1,
        strokeColor: '#fff',
        strokeWeight: 2,
        scale: 7
      }
    });

    new gm.Marker({
      position: { lat: endLat, lng: endLng },
      map: map,
      icon: {
        path: gm.SymbolPath.CIRCLE,
        fillColor: '#ef4444',
        fillOpacity: 1,
        strokeColor: '#fff',
        strokeWeight: 2,
        scale: 7
      }
    });

    // Try to get directions if service is available, otherwise fall back
    if (directionsService && directionsRenderer) {
      directionsService.route(
        {
          origin: { lat: startLat, lng: startLng },
          destination: { lat: endLat, lng: endLng },
          travelMode: gm ? gm.TravelMode.DRIVING : null
        },
        (result, status) => {
          if (gm && status === gm.DirectionsStatus.OK) {
            directionsRenderer.setDirections(result);
          } else {
            const bounds = gm ? new gm.LatLngBounds() : null;
            if (bounds) {
              bounds.extend({ lat: startLat, lng: startLng });
              bounds.extend({ lat: endLat, lng: endLng });
              map.fitBounds(bounds);
            }
            if (gm) {
              new gm.Polyline({
                path: [{ lat: startLat, lng: startLng }, { lat: endLat, lng: endLng }],
                strokeColor: '#3b82f6',
                strokeOpacity: 0.5,
                strokeWeight: 3,
                icons: [{ icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 2 }, offset: '0', repeat: '10px' }],
                map: map
              });
            }
          }
        }
      );
    } else {
      // No directions service available: ensure map fits both markers and draw fallback line
      if (gm) {
        const bounds = new gm.LatLngBounds();
        bounds.extend({ lat: startLat, lng: startLng });
        bounds.extend({ lat: endLat, lng: endLng });
        map.fitBounds(bounds);
        new gm.Polyline({
          path: [{ lat: startLat, lng: startLng }, { lat: endLat, lng: endLng }],
          strokeColor: '#3b82f6',
          strokeOpacity: 0.5,
          strokeWeight: 3,
          icons: [{ icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 2 }, offset: '0', repeat: '10px' }],
          map: map
        });
      }
    }
  }, [mapsLoaded, startLat, startLng, endLat, endLng]);

  return (
    <div ref={mapRef} style={{ width: '100%', height: '100%', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {!mapsLoaded && <div style={{ color: '#64748b', fontSize: '12px' }}>Loading Map...</div>}
    </div>
  );
};

export default function TripReport({ devices = [], onFullWidthChange, setBackAction, setPageTitle, onTrack }) {
  const [selectedImei, setSelectedImei] = useState(null);
  const [query, setQuery] = useState('');
  const [timePreset, setTimePreset] = useState('4h');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [activeRange, setActiveRange] = useState(() => buildPresetRange('4h'));
  const [loading, setLoading] = useState(false);
  const [trips, setTrips] = useState([]);

  const apiKey = useMemo(() => process.env.REACT_APP_API_KEY || (typeof window !== 'undefined' && window.REACT_APP_API_KEY) || '', []);
  const { googleMapsApiKey: googleKey } = useFleetGoogleMaps();

  useEffect(() => {
    return () => { if (setPageTitle) setPageTitle(''); };
  }, [selectedImei, setPageTitle]);

  useEffect(() => { if (onFullWidthChange) onFullWidthChange(Boolean(selectedImei)); }, [selectedImei, onFullWidthChange]);

  useEffect(() => {
    if (selectedImei && setBackAction) setBackAction(() => () => window.history.back());
    return () => { if (setBackAction) setBackAction(null); };
  }, [selectedImei, setBackAction]);
  useEffect(() => {
    const handlePopState = () => {
      const match = window.location.pathname.match(/^\/tripreport\/(.+)$/);
      setSelectedImei(match ? match[1] : null);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
  useEffect(() => { if (timePreset !== 'custom') setActiveRange(buildPresetRange(timePreset)); }, [timePreset]);

  const handleApplyCustomRange = () => {
    if (!customStart || !customEnd) { toast.warning('Select both start and end time'); return; }
    // Parse custom inputs as local time (IST), then stored as Date objects
    const start = new Date(customStart);
    const end = new Date(customEnd);
    if (!start.getTime() || !end.getTime() || start >= end) { toast.error('Invalid range'); return; }
    setActiveRange({ start, end, label: 'custom' });
  };

  useEffect(() => {
    if (!selectedImei) { setTrips([]); return; }
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const fromStamp = toUtcStamp(activeRange.start);
        const toStamp = toUtcStamp(activeRange.end);
        const url = `${process.env.REACT_APP_TRIP_REPORT_API_BASE}/trips?imeis=${selectedImei}&TimeRange=${fromStamp},${toStamp}`;
        ;
        const res = await fetch(url, { headers: apiKey ? { 'x-api-key': apiKey } : undefined });
        const json = await res.json();

        let data = [];
        if (Array.isArray(json)) data = json;
        else if (Array.isArray(json.trips)) data = json.trips;
        else if (Array.isArray(json.data)) data = json.data;
        else if (json.body) {
          try {
            const p = JSON.parse(json.body);
            data = Array.isArray(p) ? p : (p.trips || p.data || []);
          } catch { data = []; }
        }

        const normalized = data.map(t => ({
          id: t.trip_id || Math.random().toString(36).slice(2),
          imei: t.imei,
          startTime: t.trip_start_time,
          endTime: t.trip_end_time,
          duration: t.duration_seconds || 0,
          distance: t.distance_km || 0,
          startLat: t.start_latitude,
          startLng: t.start_longitude,
          endLat: t.end_latitude,
          endLng: t.end_longitude,
          startOdo: t.start_odo,
          endOdo: t.end_odo,
          createdAt: t.created_at,
          startAddress: '',
          endAddress: ''
        })).sort((a, b) => (b.startTime || 0) - (a.startTime || 0));

        if (googleKey && normalized.length > 0) {
          const limit = 10; // Enrich top 10 for mobile performance
          const head = normalized.slice(0, limit);
          const tail = normalized.slice(limit);
          const enriched = await Promise.all(head.map(async t => {
            let sAddr = '', eAddr = '';
            try {
              if (t.startLat && t.startLng) {
                const r = await fetch(`${process.env.REACT_APP_GOOGLE_GEOCODE_API_URL || 'https://maps.googleapis.com/maps/api/geocode/json'}?latlng=${t.startLat},${t.startLng}&key=${googleKey}`);
                const g = await r.json();
                sAddr = g.results?.[0]?.formatted_address || '';
              }
              if (t.endLat && t.endLng) {
                const r = await fetch(`${process.env.REACT_APP_GOOGLE_GEOCODE_API_URL || 'https://maps.googleapis.com/maps/api/geocode/json'}?latlng=${t.endLat},${t.endLng}&key=${googleKey}`);
                const g = await r.json();
                eAddr = g.results?.[0]?.formatted_address || '';
              }
            } catch { }
            return { ...t, startAddress: sAddr, endAddress: eAddr };
          }));
          if (!cancelled) setTrips([...enriched, ...tail]);
        } else if (!cancelled) {
          setTrips(normalized);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) toast.error('Failed to load trips');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [selectedImei, activeRange, apiKey, googleKey]);

  const handleExportPdf = () => {
    if (trips.length === 0) {
      toast.info('No trips to export');
      return;
    }
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.text('Fleet Trip Report', 14, 22);
    doc.setFontSize(11);
    doc.setTextColor(100);
    doc.text(`IMEI: ${selectedImei}`, 14, 30);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 36);

    const tableData = trips.map((t, i) => [
      i + 1,
      formatIstTime(t.startTime),
      formatIstTime(t.endTime),
      formatDuration(t.duration),
      `${t.distance.toFixed(2)} km`,
      t.startAddress || `${t.startLat}, ${t.startLng}`,
      t.endAddress || `${t.endLat}, ${t.endLng}`
    ]);

    autoTable(doc, {
      startY: 45,
      head: [['#', 'Start', 'End', 'Duration', 'Dist', 'Start Location', 'End Location']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [15, 118, 110] },
      styles: { fontSize: 8, cellPadding: 2 },
      columnStyles: {
        5: { cellWidth: 40 },
        6: { cellWidth: 40 }
      }
    });

    doc.save(`TripReport_${selectedImei}_${new Date().getTime()}.pdf`);
    toast.success('PDF report generated');
  };

  if (!selectedImei) {
    const filtered = query.trim() === '' ? devices : devices.filter(d =>
      String(d.name || d.deviceName || '').toLowerCase().includes(query.toLowerCase()) ||
      String(d.imei || '').includes(query)
    );

    const columns = [
      { header: '#', accessor: 'number' },
      { header: 'Name', accessor: 'name' },
      { header: 'IMEI', accessor: 'imei' },
      { header: 'Action', accessor: 'action' }
    ];

    const rows = filtered.map((d, i) => ({
      number: (
        <span className="badge fleet-table-index">
          {String(i + 1).padStart(2, '0')}
        </span>
      ),
      name: d.name || d.deviceName,
      imei: <span className="mono">{d.imei}</span>,
      action: (
        <div style={{ textAlign: 'center' }}>
          <button className="soft" onClick={() => {
            setSelectedImei(d.imei);
            window.history.pushState({}, '', `/tripreport/${d.imei}`);
          }}>
            View Trips
          </button>
        </div>
      )
    }));

    return (
      <ContentWrapper title="Trip Report">
        <div className="trip-report devices">
          <div className="devices-desktop">
            <div className="devices-header-row">
              <input
                className="devices-search-input"
                type="text"
                placeholder="Search name, IMEI…"
                value={query}
                onChange={e => setQuery(e.target.value)}
              />
            </div>

            <div className="devices-table-container fleet-table-wrapper">
              {rows.length === 0 ? (
                <div className="align-center fleet-table-empty">No trip reports found</div>
              ) : (
                <UniformTable columns={columns} data={rows} />
              )}
            </div>
          </div>

          <div className="devices-mobile trip-report-mobile-list">
            <div className="devices-header-row">
              <input
                className="devices-search-input"
                type="text"
                placeholder="Search name, IMEI…"
                value={query}
                onChange={e => setQuery(e.target.value)}
                style={{ marginBottom: '16px' }}
              />
            </div>

            <div className="mobile-devices-container">
              {filtered.length === 0 ? (
                <div className="align-center" style={{ padding: '24px', color: '#64748b', background: '#f8fafc', borderRadius: '8px' }}>
                  No trip reports found
                </div>
              ) : (
                filtered.map((d, i) => (
                  <div key={d.imei || d.id || i} className="mobile-device-card trip-report-mobile-card">
                    <div className="mobile-device-card-header">
                      <h3 className="mobile-device-name">{d.name || d.deviceName || 'Device'}</h3>
                      <span className="devices-badge">{String(i + 1).padStart(2, '0')}</span>
                    </div>

                    <div className="mobile-device-card-body">
                      <div className="mobile-device-info-row">
                        <span className="mobile-info-label">IMEI</span>
                        <span className="mobile-info-value mono">{d.imei || '-'}</span>
                      </div>
                    </div>

                    <div className="mobile-device-actions">
                      <button className="btn-details" onClick={() => {
                        setSelectedImei(d.imei);
                        window.history.pushState({}, '', `/tripreport/${d.imei}`);
                      }}>
                        View Trips
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper title="Trip Report">
      <div className="trip-report-page">
        <div className="trip-header-row">
          <div className="trip-header-left">
            <div className="trip-header-subtitle">IMEI: {selectedImei}</div>
          </div>
          <div className="trip-header-actions">
            <div className="trip-controls" style={{ marginBottom: 0 }}>
              <div className="time-select-wrapper">
                {/* <select value={timePreset} onChange={e => setTimePreset(e.target.value)}>
                  <option value="4h">Last 4 Hours</option>
                  <option value="12h">Last 12 Hours</option>
                  <option value="24h">Last 24 Hours</option>
                  <option value="7d">Last 7 Days</option>
                  <option value="custom">Custom Range</option>
                </select> */}
                <FilterDropdown
                  value={timePreset}
                  onChange={setTimePreset}
                  options={[
                    { value: '4h', label: 'Last 4 Hours' },
                    { value: '12h', label: 'Last 12 Hours' },
                    { value: '24h', label: 'Last 24 Hours' },
                    { value: '7d', label: 'Last 7 Days' },
                    { value: 'custom', label: 'Custom Range' }
                  ]}
                />
              </div>
            </div>
            <button className="action-icon-btn pdf-btn" onClick={handleExportPdf} title="Export PDF">
              <span style={{ fontWeight: '600' }}>Export PDF</span>
            </button>
          </div>
        </div>

        {timePreset === 'custom' && (
          <div className="trip-controls">
            <div className="trip-custom-range">
              <label>Start (IST)<input type="datetime-local" value={customStart} onChange={e => setCustomStart(e.target.value)} /></label>
              <label>End (IST)<input type="datetime-local" value={customEnd} onChange={e => setCustomEnd(e.target.value)} /></label>
              <button type="button" className="apply-btn" onClick={handleApplyCustomRange}>Apply</button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="loading-container"><div className="loader" /><p>Loading trip details...</p></div>
        ) : trips.length === 0 ? (
          <div className="no-trips">No trips found for the selected range.</div>
        ) : (
          <>
            <div className="trip-list trip-list--desktop">
              {trips.map(t => (
                <div key={t.id} className="trip-card-v2">
                  <div className="trip-summary-v2">
                    <div className="summary-item">
                      <span className="label">DISTANCE</span>
                      <span className="value distance">{t.distance.toFixed(2)} km</span>
                    </div>
                    <div className="summary-item">
                      <span className="label">DURATION</span>
                      <span className="value">{formatDuration(t.duration)}</span>
                    </div>
                  </div>

                  <div className="trip-timeline">
                    <div className="timeline-point start">
                      <div className="time">{formatIstTime(t.startTime)}</div>
                      <div className="location">{t.startAddress || `${t.startLat.toFixed(5)}, ${t.startLng.toFixed(5)}`}</div>
                    </div>
                    <div className="timeline-duration">{formatDuration(t.duration)}</div>
                    <div className="timeline-point end">
                      <div className="time">{formatIstTime(t.endTime)}</div>
                      <div className="location">{t.endAddress || `${t.endLat.toFixed(5)}, ${t.endLng.toFixed(5)}`}</div>
                    </div>
                  </div>

                  <div className="trip-map-preview">
                    <TripMapPreview
                      startLat={t.startLat} startLng={t.startLng}
                      endLat={t.endLat} endLng={t.endLng}
                    />
                    <button className="open-map-btn" onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&origin=${t.startLat},${t.startLng}&destination=${t.endLat},${t.endLng}&travelmode=driving`, '_blank')}>
                      Open Full Map
                    </button>
                  </div>

                  <div className="trip-metrics-v2">
                    <div className="metrics-grid">
                      <div className="metric-item">
                        <span className="label">Start Odo</span>
                        <span className="value">{t.startOdo || '-'}</span>
                      </div>
                      <div className="metric-item">
                        <span className="label">End Odo</span>
                        <span className="value">{t.endOdo || '-'}</span>
                      </div>
                      <div className="metric-item">
                        <span className="label">Start Coord</span>
                        <span className="value">{t.startLat.toFixed(4)}, {t.startLng.toFixed(4)}</span>
                      </div>
                      <div className="metric-item">
                        <span className="label">End Coord</span>
                        <span className="value">{t.endLat.toFixed(4)}, {t.endLng.toFixed(4)}</span>
                      </div>
                    </div>
                    {t.distance < 0.05 && <div className="warning-badge">Very Short Trip</div>}
                    <div className="created-at">Recorded: {new Date(t.createdAt * 1000).toLocaleString()}</div>
                  </div>
                </div>
              ))}
            </div>

            <div className="trip-list trip-list--mobile">
              {trips.map(t => (
                <div key={t.id} className="trip-card-mobile">
                  <div className="trip-mobile-top">
                    <div>
                      <div className="trip-mobile-label">Distance</div>
                      <div className="trip-mobile-value distance">{t.distance.toFixed(2)} km</div>
                    </div>
                    <div>
                      <div className="trip-mobile-label">Duration</div>
                      <div className="trip-mobile-value">{formatDuration(t.duration)}</div>
                    </div>
                  </div>

                  <div className="trip-mobile-body">
                    <div className="trip-mobile-row">
                      <span className="trip-mobile-row-label">Start</span>
                      <span className="trip-mobile-row-value">{formatIstTime(t.startTime)}</span>
                    </div>
                    <div className="trip-mobile-row">
                      <span className="trip-mobile-row-label">End</span>
                      <span className="trip-mobile-row-value">{formatIstTime(t.endTime)}</span>
                    </div>
                    <div className="trip-mobile-row trip-mobile-row--stacked">
                      <span className="trip-mobile-row-label">Route</span>
                      <span className="trip-mobile-row-value">{t.startAddress || `${t.startLat.toFixed(4)}, ${t.startLng.toFixed(4)}`} → {t.endAddress || `${t.endLat.toFixed(4)}, ${t.endLng.toFixed(4)}`}</span>
                    </div>
                  </div>

                  <div className="trip-mobile-map">
                    <TripMapPreview
                      startLat={t.startLat} startLng={t.startLng}
                      endLat={t.endLat} endLng={t.endLng}
                    />
                  </div>

                  <div className="trip-mobile-actions">
                    <button className="btn-details" onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&origin=${t.startLat},${t.startLng}&destination=${t.endLat},${t.endLng}&travelmode=driving`, '_blank')}>
                      Open Map
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </ContentWrapper>
  );
}
